/* VORAX — header predictive search (Dawn's PredictiveSearch, reworked).
   Changes from Dawn:
   - asks Shopify for products, categories, suggestions and pages separately (6 each), puts out-of-stock
     products last, and matches on title, brand, product type, tags and SKU (not the description body,
     which made results noisy)
   - highlights the typed words in product, category and page titles
   - arrow keys move through every option, including "Search all products"
   - remembers the last 6 searches on this device (localStorage) and shows them, with popular searches
     and categories, before anything is typed
   - fixed the cache key, which only replaced the first space
   Markup: sections/predictive-search.liquid and snippets/header-search.liquid. Styles: assets/vorax-search.css. */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.recentKey = 'vx-recent-searches';

    this.setupEventListeners();
    this.toggleEmpty();
    this.renderRecent();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', () => this.toggleEmpty());
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  toggleEmpty() {
    this.toggleAttribute('data-empty', !this.getQuery().length);
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    this.toggleEmpty();

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    const q = this.getQuery();
    if (!q.length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }
    this.saveRecent(q);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
      this.toggleEmpty();
    }
  }

  onClick(event) {
    const all = event.target.closest('[data-vxps-all]');
    if (all) {
      event.preventDefault();
      this.saveRecent(this.getQuery());
      this.input.form.submit();
      return;
    }
    const clear = event.target.closest('[data-vxq-clear]');
    if (clear) {
      event.preventDefault();
      try { localStorage.removeItem(this.recentKey); } catch (e) {}
      this.renderRecent();
      this.input.focus();
      return;
    }
    const pick = event.target.closest('[data-vxps-term]');
    if (pick) this.saveRecent(pick.getAttribute('data-vxps-term') || this.getQuery());
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    this.toggleEmpty();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    if (el) el.textContent = `Search all products for “${newTerm}”`;
  }

  options() {
    return Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]')).filter((el) => el.offsetParent !== null);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const opts = this.options();
    if (!opts.length) return;
    const selected = this.querySelector('[aria-selected="true"]');
    let i = opts.indexOf(selected);
    if (direction === 'up' && i === -1) return;
    i = direction === 'up' ? (i <= 0 ? opts.length - 1 : i - 1) : i === opts.length - 1 ? 0 : i + 1;

    this.statusElement.textContent = '';
    if (selected) selected.setAttribute('aria-selected', false);
    const next = opts[i];
    next.setAttribute('aria-selected', true);
    next.scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', next.id);
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selected) selected.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase().replace(/\s+/g, '-');
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const params = new URLSearchParams({
      q: searchTerm,
      section_id: 'predictive-search',
      'resources[type]': 'product,collection,query,page,article',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,vendor,tag,variants.title,variants.sku',
    });

    fetch(`${routes.predictive_search_url}?${params.toString()}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.highlight();
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  highlight() {
    const words = this.getQuery()
      .split(/\s+/)
      .filter((w) => w.length > 1)
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!words.length) return;
    const re = new RegExp(`(${words.join('|')})`, 'gi');
    this.predictiveSearchResults.querySelectorAll('.vxps-hl').forEach((el) => {
      const text = el.textContent;
      if (!re.test(text)) return;
      re.lastIndex = 0;
      el.textContent = '';
      text.split(re).forEach((part, i) => {
        if (!part) return;
        if (i % 2) {
          const m = document.createElement('mark');
          m.textContent = part;
          el.appendChild(m);
        } else {
          el.appendChild(document.createTextNode(part));
        }
      });
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    this.setLiveRegionText(count ? count.textContent.trim() : '');
  }

  getResultsMaxHeight() {
    const bottom = this.input.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(window.innerHeight - bottom - 24, 240);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
    this.toggleEmpty();
  }

  /* recent searches, kept on this device only */
  getRecent() {
    try {
      const list = JSON.parse(localStorage.getItem(this.recentKey) || '[]');
      return Array.isArray(list) ? list.filter((t) => typeof t === 'string') : [];
    } catch (e) {
      return [];
    }
  }

  saveRecent(term) {
    term = (term || '').trim();
    if (term.length < 2) return;
    const list = [term].concat(this.getRecent().filter((t) => t.toLowerCase() !== term.toLowerCase())).slice(0, 6);
    try { localStorage.setItem(this.recentKey, JSON.stringify(list)); } catch (e) {}
    this.renderRecent();
  }

  renderRecent() {
    const box = this.querySelector('[data-vxq-recent]');
    if (!box) return;
    const list = this.getRecent();
    const wrap = box.closest('[data-vxq-recent-wrap]');
    if (wrap) wrap.hidden = !list.length;
    box.textContent = '';
    const base = (window.routes && routes.search_url) || '/search';
    list.forEach((t) => {
      const a = document.createElement('a');
      a.className = 'vxq-chip vxq-chip--recent';
      a.href = `${base}?q=${encodeURIComponent(t)}&options%5Bprefix%5D=last`;
      a.setAttribute('data-vxps-term', t);
      a.textContent = t;
      box.appendChild(a);
    });
  }
}

customElements.define('predictive-search', PredictiveSearch);
